<script>
import Location from "@/components/modals/property-location.vue";
import Employee from "@/components/modals/employee-hris.vue";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import MaskedInput from "vue-text-mask";

export default {
  name: "CreateStockRequestModal",
  components: {
    Location,
    Employee,
    MaskedInput
  },
  props: {
    modalId: {
      type: String,
      default: "modal-create-stock-request"
    },
    modalTitle: {
      type: String,
      default: "Create Stock Request"
    }
  },
  data() {
    return {
      submitted: false,
      isSubmitting: false,
      autoGenerateRIS: true,
      loadingRIS: false,
      currentPageItem: 1,
      setAllEmployee: false,
      setAllLocation: false,
      selectedItemIndex: -1,
      quantityMask: createNumberMask({
        prefix: "",
        includeThousandsSeparator: false
      }),
      statusOptions: [
        { text: "Draft", value: "DRAFT" },
        { text: "Pending", value: "PENDING" }
      ],
      form: {
        status: "DRAFT",
        requisitionNumber: "",
        requisitionItems: []
      }
    };
  },
  computed: {
    activeItem() {
      if (this.form.requisitionItems && this.form.requisitionItems.length) {
        const index = this.currentPageItem - 1;
        return this.form.requisitionItems[index] || null;
      }
      return null;
    }
  },
  watch: {
    autoGenerateRIS(newVal) {
      if (newVal) {
        this.generateRISNumber();
      } else {
        this.form.requisitionNumber = "";
      }
    }
  },
  methods: {
    async open(selectedStocks = []) {
      this.submitted = false;
      this.isSubmitting = false;
      this.currentPageItem = 1;
      this.setAllEmployee = false;
      this.setAllLocation = false;
      this.autoGenerateRIS = true;

      this.form = {
        status: "DRAFT",
        requisitionNumber: "",
        requisitionItems: selectedStocks.map((stock) => {
          const maxQty = stock.balance ? stock.balance : 0;

          return {
            requisitionId: 0,
            iarItemId: stock.iarItemId || 0,
            stockItemId: stock.id || stock.stockItemId || 0,
            autoCredit: true,
            quantity: maxQty > 0 ? "1" : "0",
            endUserId: "",
            note: "",
            propertyLocationId: 0,
            // UI helper attributes
            stockNumber: stock.stockNumber || "N/A",
            stockName: stock.name || stock.itemName || "",
            description: stock.description || "",
            unit: stock.unit || "unit",
            maxQuantity: maxQty,
            employeeName: "",
            location: ""
          };
        })
      };

      await this.generateRISNumber();
      this.$bvModal.show(this.modalId);
    },
    checkCredit(transaction) {
      if (
        parseFloat(transaction.quantity) > parseFloat(transaction.maxQuantity)
      )
        transaction.quantity = transaction.maxQuantity.toString();
    },
    async generateRISNumber() {
      this.loadingRIS = true;
      try {
        const campusId = this.$store.state.data.selectedCampus.id;
        const { data: response } = await this.$store.dispatch(
          "requisition/GenerateRequisitionNumber",
          campusId
        );
        if (response && !response.error) {
          this.form.requisitionNumber = typeof response === "string" ? response : response.data || "";
        } else {
          this.form.requisitionNumber = "";
        }
      } catch (error) {
        this.form.requisitionNumber = "";
      } finally {
        this.loadingRIS = false;
      }
    },
    removeItem(index) {
      this.form.requisitionItems.splice(index, 1);
      if (this.currentPageItem > this.form.requisitionItems.length) {
        this.currentPageItem = Math.max(1, this.form.requisitionItems.length);
      }
      if (this.form.requisitionItems.length === 0) {
        this.$bvModal.hide(this.modalId);
      }
    },
    getEmployee(index) {
      this.selectedItemIndex = index;
      if (this.$refs.employee) {
        this.$refs.employee.resetData();
      }
      this.$bvModal.show("externalEmployee-modal");
    },
    dropEmployee(data) {
      if (!data) return;
      const empName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
      const idNo = data.idNo ? data.idNo.toString() : (data.id ? data.id.toString() : "");

      if (this.setAllEmployee) {
        this.form.requisitionItems.forEach((item) => {
          item.endUserId = idNo;
          item.employeeName = empName;
        });
      } else if (this.selectedItemIndex >= 0 && this.form.requisitionItems[this.selectedItemIndex]) {
        const item = this.form.requisitionItems[this.selectedItemIndex];
        item.endUserId = idNo;
        item.employeeName = empName;
      }
    },
    assignToAllEmployee(item) {
      if (this.setAllEmployee) {
        this.form.requisitionItems.forEach((i) => {
          i.endUserId = item.endUserId;
          i.employeeName = item.employeeName;
        });
      }
    },
    getLocation(index) {
      this.selectedItemIndex = index;
      this.$bvModal.show("location-modal");
      if (this.$refs.location) {
        this.$refs.location.getData(this.$store.state.data.selectedCampus.id);
      }
    },
    dropLocation(data) {
      if (!data || !data.location) return;
      const locId = data.location.id || 0;
      const locName = data.location.location || "";

      if (this.setAllLocation) {
        this.form.requisitionItems.forEach((item) => {
          item.propertyLocationId = locId;
          item.location = locName;
        });
      } else if (this.selectedItemIndex >= 0 && this.form.requisitionItems[this.selectedItemIndex]) {
        const item = this.form.requisitionItems[this.selectedItemIndex];
        item.propertyLocationId = locId;
        item.location = locName;
      }
    },
    assignToAllLocation(item) {
      if (this.setAllLocation) {
        this.form.requisitionItems.forEach((i) => {
          i.propertyLocationId = item.propertyLocationId;
          i.location = item.location;
        });
      }
    },
    checkForm() {
      let errors = [];
      if (!this.form.requisitionNumber || this.form.requisitionNumber.trim() === "") {
        this.showToast("Requisition / RIS Number is required.", "error");
        return true;
      }

      if (!this.form.requisitionItems || this.form.requisitionItems.length === 0) {
        this.showToast("No stock items selected.", "error");
        return true;
      }

      this.form.requisitionItems.forEach((item, index) => {
        const itemNumber = index + 1;
        const qty = parseFloat(item.quantity);
        if (isNaN(qty) || qty <= 0) {
          errors.push(`Item #${itemNumber}: Quantity must be greater than 0.`);
        } else if (item.maxQuantity > 0 && qty > item.maxQuantity) {
          errors.push(`Item #${itemNumber}: Quantity exceeds available balance (${item.maxQuantity}).`);
        }
        if (!item.endUserId || item.endUserId.trim() === "") {
          errors.push(`Item #${itemNumber}: End user is required.`);
        }
      });

      if (errors.length > 0) {
        this.showToast(errors[0], "error");
        return true;
      }

      return false;
    },
    onSubmit() {
      this.submitted = true;
      if (this.checkForm()) return;

      const payload = {
        status: this.form.status,
        requisitionNumber: this.form.requisitionNumber,
        requisitionItems: this.form.requisitionItems.map((item) => ({
          requisitionId: item.requisitionId || 0,
          iarItemId: item.iarItemId || 0,
          stockItemId: item.stockItemId || 0,
          autoCredit: Boolean(item.autoCredit),
          quantity: parseFloat(item.quantity) || 0,
          endUserId: String(item.endUserId || ""),
          note: String(item.note || ""),
          propertyLocationId: parseInt(item.propertyLocationId) || 0
        }))
      };

      this.$emit("submit", payload);
    },
    hide() {
      this.$bvModal.hide(this.modalId);
    }
  }
};
</script>

<template>
  <div>
    <b-modal :id="modalId" :title="modalTitle" size="lg" title-class="font-18" hide-footer no-close-on-backdrop>
      <form @submit.prevent="onSubmit" class="needs-validation">
        <!-- Top Section: RIS Number -->
        <div class="row mb-3">
          <div class="col-md-12">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label font-size-13 mb-0">Requisition / RIS Number</label>
              <div class="form-check form-checkbox-outline form-check-success mb-0">
                <input type="checkbox" id="autoGenerateRIS" class="form-check-input" v-model="autoGenerateRIS" />
                <label for="autoGenerateRIS" class="form-check-label font-size-12">
                  Auto Generate
                </label>
              </div>
            </div>
            <div class="position-relative">
              <input id="requisitionNumber" type="text" v-model="form.requisitionNumber"
                placeholder="Enter RIS Number..." class="form-control" :disabled="autoGenerateRIS" :class="{
                  'is-invalid': submitted && !form.requisitionNumber
                }" autocomplete="off" />
              <div v-if="loadingRIS" class="position-absolute end-0 top-50 translate-middle-y me-2">
                <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
              </div>
            </div>
            <div v-if="submitted && !form.requisitionNumber" class="invalid-feedback d-block">
              <span>RIS Number is required.</span>
            </div>
          </div>
        </div>

        <hr class="my-2" />

        <!-- Pagination for Multiple Items -->
        <div v-if="form.requisitionItems && form.requisitionItems.length > 1"
          class="d-flex justify-content-between align-items-center mb-2">
          <label class="font-size-14 text-muted mb-0">
            Item {{ currentPageItem }} of {{ form.requisitionItems.length }}
          </label>
          <div class="dataTables_paginate paging_simple_numbers">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination v-model="currentPageItem" :total-rows="form.requisitionItems.length" :per-page="1"
                size="sm"></b-pagination>
            </ul>
          </div>
        </div>

        <!-- Active Stock Item Form -->
        <div v-if="activeItem" class="border rounded p-3 mb-3">
          <div class="d-flex justify-content-between align-items-center pb-2 border-bottom mb-3">
            <div class="d-flex flex-column">
              <span class="fw-bold">{{ activeItem.stockName }}</span>
              <span class="me-2">Stock No: {{ activeItem.stockNumber }}</span>
            </div>
            <div class="d-flex align-items-center">
              <button type="button" class="btn btn-outline-danger" title="Remove item"
                @click="removeItem(currentPageItem - 1)">
                <i class="bx bx-trash font-size-18"></i>
              </button>
            </div>
          </div>

          <div class="d-flex flex-column mb-3">
            <!-- Quantity with Masked Input -->
            <div class="d-flex justify-content-between align-items-center">
              <label class="form-label font-size-13 mb-1">
                Quantity <span class="text-danger">*</span>
                <small class="text-muted">(Max: {{ activeItem.maxQuantity }})</small>
              </label>
              <div class="form-check form-checkbox-outline form-check-success">
                <input type="checkbox" :id="`autoCredit-${currentPageItem}`" class="form-check-input"
                  v-model="activeItem.autoCredit" />
                <label :for="`autoCredit-${currentPageItem}`" class="form-check-label font-size-13">
                  Auto Credit
                </label>
              </div>
            </div>

            <masked-input v-if="activeItem.maxQuantity && activeItem.maxQuantity > 0" id="credit" type="text"
              v-model="activeItem.quantity" placeholder="Enter Quantity..." class="form-control" :class="{
                'is-invalid':
                  submitted &&
                  (activeItem.quantity == null || activeItem.quantity == '' || parseFloat(activeItem.quantity) <= 0)
              }" autocomplete="off" :mask="quantityMask" @input="checkCredit(activeItem)"></masked-input>
            <input v-else id="credit" type="text" v-model="activeItem.quantity" placeholder="Enter Quantity..."
              class="form-control" :class="{
                'is-invalid':
                  submitted &&
                  (activeItem.quantity == null || activeItem.quantity == '')
              }" disabled autocomplete="off" />

            <div v-if="
              submitted &&
              (!activeItem.quantity ||
                activeItem.quantity === '' ||
                parseFloat(activeItem.quantity) <= 0)
            " class="invalid-feedback d-block">
              <span>Quantity must be greater than 0.</span>
            </div>
          </div>

          <!-- End User -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label font-size-13 mb-0">
                End User <span class="text-danger">*</span>
              </label>
              <div v-if="form.requisitionItems.length > 1"
                class="form-check form-checkbox-outline form-check-success mb-0">
                <input type="checkbox" :id="`setAllEmployee-${currentPageItem}`" class="form-check-input"
                  v-model="setAllEmployee" @change="assignToAllEmployee(activeItem)" />
                <label :for="`setAllEmployee-${currentPageItem}`" class="form-check-label font-size-12">
                  Assign to all items
                </label>
              </div>
            </div>
            <b-input-group>
              <template #append>
                <b-button @click="getEmployee(currentPageItem - 1)" variant="outline-info">
                  <i class="mdi mdi-clipboard-text-search-outline"></i> Search
                </b-button>
              </template>
              <input type="text" disabled placeholder="Select Employee..." class="form-control" :class="{
                'is-invalid': submitted && !activeItem.endUserId
              }" :value="activeItem.employeeName ? `${activeItem.employeeName} (${activeItem.endUserId})` : ''" />
            </b-input-group>
            <div v-if="submitted && !activeItem.endUserId" class="invalid-feedback d-block">
              <span>End User is required.</span>
            </div>
          </div>

          <!-- Property Location -->
          <div class="mb-3">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label font-size-13 mb-0">Property Location</label>
              <div v-if="form.requisitionItems.length > 1"
                class="form-check form-checkbox-outline form-check-success mb-0">
                <input type="checkbox" :id="`setAllLocation-${currentPageItem}`" class="form-check-input"
                  v-model="setAllLocation" @change="assignToAllLocation(activeItem)" />
                <label :for="`setAllLocation-${currentPageItem}`" class="form-check-label font-size-12">
                  Assign to all items
                </label>
              </div>
            </div>
            <b-input-group>
              <template #append>
                <b-button @click="getLocation(currentPageItem - 1)" variant="outline-info">
                  <i class="mdi mdi-clipboard-text-search-outline"></i> Search
                </b-button>
              </template>
              <input type="text" disabled placeholder="Select Property Location..." class="form-control"
                :value="activeItem.location" />
            </b-input-group>
          </div>

          <!-- Note -->
          <div class="mb-2">
            <label class="form-label font-size-13 mb-1">Note</label>
            <textarea rows="2" v-model="activeItem.note" placeholder="Enter note / remarks..." class="form-control"
              autocomplete="off"></textarea>
          </div>
        </div>

        <div v-else class="text-center py-4 text-muted">
          <i class="fas fa-exclamation-triangle font-size-24 text-warning mb-2"></i>
          <p class="mb-0">No stock items available in request.</p>
        </div>

        <!-- Footer Action -->
        <hr class="my-2" />
        <div class="d-flex justify-content-end mt-3">
          <button type="submit" class="btn btn-primary" :disabled="isSubmitting || form.requisitionItems.length === 0">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1" role="status"></span>
            Create Request ({{ form.requisitionItems.length }} {{ form.requisitionItems.length === 1 ? 'item' : 'items'
            }})
          </button>
          <button type="button" class="btn btn-link" @click="hide">
            Cancel
          </button>
        </div>
      </form>
    </b-modal>

    <!-- Sub-modals for Employee and Location Selection -->
    <Employee ref="employee" @dropData="dropEmployee($event)"></Employee>
    <Location ref="location" @dropData="dropLocation($event)"></Location>
  </div>
</template>
